import memoize from '@jetbrains/ring-ui/components/global/memoize'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import type {ReactElement, ReactNode} from 'react'
import {Children, useEffect, useMemo, useState} from 'react'

import useProjectHierarchy from '../../../../../hooks/useProjectsHierarchy'
import type {BuildTypeId, NormalizedBuild, ProjectId} from '../../../../../types'
import {stringifyId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import type {KeyValue} from '../../../../../utils/object'
import Tree from '../../../../common/Tree/Tree'
import {EXPAND_ARROW_WIDTH, TREE_INDENT_WIDTH} from '../../../../common/Tree/Tree.consts'
import type {Node} from '../../../../common/Tree/Tree.types'
import type {TreeNodeProps} from '../../../../common/Tree/TreeNode/TreeNode'

import ChangeBuildsGrid from './ChangeBuildsGrid'
import ChangeBuildType from './ChangeBuildType/ChangeBuildType'
import ChangeBuildTypeDetails from './ChangeBuildTypeDetails/ChangeBuildTypeDetails'
import ProjectLink from './ProjectLink/ProjectLink'

import styles from './ChangeBuilds.css'

type Props = {
  buildTypeIds: string[]
  buildsHash: KeyValue<BuildTypeId, NormalizedBuild[]>
  isDeployment: boolean | undefined
}

type ChangeBuildsTreeChildrenProps = {
  children?: ReactNode
  level?: number
}

const LEFT_OFFSET = 16

function ChangeBuildsTreeChildren({children, level = 0}: ChangeBuildsTreeChildrenProps) {
  const childrenArray = Children.toArray(children) as ReactElement<TreeNodeProps>[]
  return (
    <>
      {childrenArray.filter(child => child.props.value.startsWith('project:'))}
      <ChangeBuildsGrid
        style={{marginLeft: `${EXPAND_ARROW_WIDTH + LEFT_OFFSET + TREE_INDENT_WIDTH * level}px`}}
      >
        {childrenArray.filter(child => child.props.value.startsWith('buildType:'))}
      </ChangeBuildsGrid>
    </>
  )
}
const childrenComponent = <ChangeBuildsTreeChildren />

function ChangeBuildsTree({buildTypeIds, buildsHash, isDeployment}: Props) {
  const renderBuildType = useMemo(
    () =>
      memoize((buildTypeId: BuildTypeId) => (
        <ChangeBuildType
          key={`row:${stringifyId(buildTypeId)}`}
          buildIds={buildsHash[buildTypeId]?.map(build => build.id) ?? emptyArray}
          buildTypeId={buildTypeId}
          isDeployment={isDeployment}
          inHierarchy
        />
      )),
    [buildsHash, isDeployment],
  )
  const renderBuildTypeDetails = useMemo(
    () =>
      memoize((buildTypeId: BuildTypeId) => (
        <ChangeBuildTypeDetails
          key={`details:${stringifyId(buildTypeId)}`}
          buildIds={buildsHash[buildTypeId]?.map(build => build.id) ?? emptyArray}
          nested
        />
      )),
    [buildsHash],
  )
  const renderProject = useMemo(
    () => memoize((projectId: ProjectId) => <ProjectLink projectId={projectId} />),
    [],
  )
  const {tree, ready} = useProjectHierarchy(buildTypeIds, ({projectId, buildTypeId}) => {
    if (buildTypeId != null) {
      return {
        value: `buildType:${buildTypeId}`,
        className: styles.buildType,
        component: renderBuildType(buildTypeId),
        detailsComponent: renderBuildTypeDetails(buildTypeId),
        expandable: false,
      }
    }
    if (projectId != null) {
      return {
        value: `project:${projectId}`,
        component: renderProject(projectId),
        childrenComponent,
      }
    }
    return {value: ''}
  })
  const [expandState, setExpandState] = useState<ReadonlyArray<string>>()
  useEffect(() => {
    if (!ready || expandState != null) {
      return
    }
    const values: string[] = []
    function processNode(node: Node) {
      values.push(node.value)
      node.children?.forEach(processNode)
    }
    tree.forEach(processNode)
    setExpandState(values)
  }, [tree, expandState, ready])

  return ready ? (
    <Tree
      className={styles.tree}
      checkable={false}
      labelId="change-page-title"
      nodes={tree}
      expanded={expandState}
      expandOnlyOneLeaf={false}
      onExpand={setExpandState}
    />
  ) : (
    <LoaderInline>{'Loading...'}</LoaderInline>
  )
}

export default ChangeBuildsTree
